import { LpcSheet } from "game-common/lpc/lpc"
import { contentGeneration } from "game-common/content/catalog"
import { Latch } from "game-common/models"
import { Callback } from "game-common/util"
import { AnimatedSprite, Graphics, Loader, Resource, Sprite, Texture, Rectangle } from "pixi.js"

import { getRendererApp, getRendererGeneration } from "../renderer_app"
import { spriteSheetLoader } from "./spritesheet_loader"
import { createHeldObjectSprite, heldObjectBehindBody, LpcHeldObject } from "./lpc_held_object"

/**
 * Per-sprite inputs that change what gets BAKED (as opposed to how the baked frames are drawn),
 * so they are part of the cache key.
 *
 * Declared here rather than in lpc_composite_sprite, which is its main consumer, because it is
 * part of this module's signature and importing it from there -- even as a type -- puts the
 * entity manager, the shatter effect and the renderer class in the type graph of everything that
 * touches the generator. Re-exported from lpc_composite_sprite for callers that expect it there.
 */
export type LpcRenderProps = {
    bodyFrame?: { w: number; h: number }
    isSubmerged?: boolean
    heldObject?: LpcHeldObject
    /** Authoring previews supply independent source textures and a content-derived cache key. */
    frameOverrides?: { key: string; sheets: Record<string, Texture[]> }
}

const INTERVAL = 20

// Logical body coordinates use the source frame's centre, regardless of atlas default anchors.
export const HALF_FRAME = 32

interface Job {
    process: () => void
    getResult: () => LpcJobResult | null
    getKey: () => string
    // True once the requesting sprite has been discarded (e.g. its entity was torn down on a room
    // change). Such jobs are dropped from the queue without baking so live jobs behind them aren't
    // delayed a tick each.
    isCancelled: () => boolean
}

export class LpcJobResult {
    sprite?: Sprite
    animation?: AnimatedSprite
    // Cache key (loadout+action+submerged) this result was cloned from. Used to refcount live
    // clones so eviction never destroys textures still referenced on screen.
    key?: string

    // Bake-region origin in native body-frame coordinates; shared across all frames of this action.
    // Every clone, including static and scaled ones, applies the same measured correction.
    offsetX: number = 0
    offsetY: number = 0

    constructor({ animation, sprite }: { animation?: AnimatedSprite; sprite?: Sprite }) {
        this.sprite = sprite
        this.animation = animation
    }

    play = (startFrame = 0) => {
        if (this.animation) {
            this.animation.visible = true
            this.animation.gotoAndPlay(startFrame)
        }
        if (this.sprite) {
            this.sprite.visible = true
        }
    }

    stop = () => {
        if (this.animation) {
            this.animation.visible = false
            this.animation.stop()
        }
        if (this.sprite) {
            this.sprite.visible = false
        }
    }

    /**
     * Show this action FROZEN on its last frame -- the pose a one-shot animation LEAVES the body
     * in, rather than the one it starts from. The LPC `hurt` row is a collapse, so its final frame
     * is a body lying face-down: that single frame is the whole "fallen" depiction.
     *
     * Distinct from `play()`, which starts at frame 0 by default. A body that was already down when we
     * first laid eyes on it must not replay the fall it took before we got there.
     */
    holdFinalFrame = () => {
        if (this.animation) {
            this.animation.visible = true
            this.animation.gotoAndStop(Math.max(0, this.animation.totalFrames - 1))
        }
        if (this.sprite) {
            this.sprite.visible = true
        }
    }

    fragmentBase = () => {
        if (this.animation) {
            const f: Texture = this.animation.textures[this.animation.currentFrame] as any
            return f
        }
        if (this.sprite) {
            return this.sprite.texture
        }
    }

    /**
     * Play this action BACKWARDS, from its last frame to its first, and stop there.
     *
     * The LPC `hurt` row is a collapse, so running it in reverse is a character pushing itself up
     * off the ground -- a get-up animation for free, with no extra art, no extra bake and no second
     * cache entry. PIXI's AnimatedSprite takes a negative `animationSpeed` natively and, with
     * `loop` off, stops at frame 0 and fires `onComplete`, which is exactly the handoff back to
     * standing.
     *
     * Safe to mutate `animationSpeed` here: every live sprite gets its OWN AnimatedSprite wrapper
     * (see cloneResult) and only the baked textures are shared. The caller restores the sign, since
     * this same wrapper is reused the next time the action plays forwards.
     */
    playReverse = () => {
        if (this.animation) {
            this.animation.visible = true
            this.animation.loop = false
            this.animation.animationSpeed = -Math.abs(this.animation.animationSpeed)
            this.animation.gotoAndPlay(Math.max(0, this.animation.totalFrames - 1))
        }
        if (this.sprite) {
            this.sprite.visible = true
        }
    }

    recolor = (color: number) => {
        if (this.animation) {
            this.animation.tint = color
        }
        if (this.sprite) {
            this.sprite.tint = color
        }
    }
}

class SynchronousJob implements Job {
    private generation = contentGeneration()
    private rendererGeneration = getRendererGeneration()
    action: any
    lpcRenderMeta: any
    lpcRenderProps: LpcRenderProps
    descriptors: LpcSheet[]
    result: LpcJobResult
    callback: Callback<LpcJobResult>
    cancelled?: () => boolean
    startTs: number
    key: string
    // Set while walking the descriptors on the BAKE path only: true when a `type: "oversize"`
    // descriptor's sheet actually contributes frames to THIS action. The gate matters -- entities
    // carry their oversize weapon descriptor during walk and idle too, where that sheet has no
    // frames and the composite stays 64x64. Consumed once, to stamp the canonical entry's offset;
    // clones read the offset from there, never from this flag (which is undefined on a cache hit).

    static textureCache: Map<string, Texture> = new Map()
    static animationCache: Map<string, LpcJobResult> = new Map()

    // Number of live (on-screen) clones per cache key. Only keys at zero may be evicted; this is
    // what makes destroying baked textures safe -- they are shared by every live clone.
    static refCounts: Map<string, number> = new Map()

    // Upper bound on cached loadout+action entries. Beyond this, unreferenced entries are evicted
    // oldest-first and their baked RenderTextures destroyed (generateTexture textures are only
    // reclaimed by explicit destroy(), never by GC).
    static maxCachedAnimations = 512

    // Build a fresh, independent display object from a cached result, reusing its baked textures.
    // Used for BOTH cache hits and the first (miss) caller, so the cached entry is never itself
    // handed to a live sprite -- only clones are, keeping the canonical safe to evict.
    static cloneResult = (cached: LpcJobResult, source: any, key: string): LpcJobResult => {
        const { scale = 1, speed = 0.267, color = 0xffffff } = source
        let animation: AnimatedSprite
        let sprite: Sprite
        const textures = (cached.animation?.textures ?? (cached.sprite ? [cached.sprite.texture] : [])) as Texture[]
        if (source.animated !== false) {
            animation = new AnimatedSprite(textures)
            animation.scale.set(scale)
            animation.animationSpeed = speed
            animation.visible = false
            animation.tint = color
            // Read off the cached entry, never recomputed here -- see LpcJobResult.offsetX.
            animation.position.set(cached.offsetX * scale, cached.offsetY * scale)
        }
        else {
            sprite = new Sprite(textures[0])
            sprite.tint = color
            sprite.scale.set(scale)
            sprite.position.set(cached.offsetX * scale, cached.offsetY * scale)
            sprite.visible = false
        }
        const result = new LpcJobResult({ animation, sprite })
        result.key = key
        result.offsetX = cached.offsetX
        result.offsetY = cached.offsetY
        return result
    }

    // The cache key for a loadout+action -- shared by every sprite with the same descriptors, action
    // and submerged flag. Single source of truth for both generate() and request-time retains.
    static computeKey = (descriptors: LpcSheet[], action: any, lpcRenderProps: LpcRenderProps): string =>
        `generation:${contentGeneration()}:renderer:${getRendererGeneration()}:` + JSON.stringify(descriptors) + "_" + action + lpcRenderProps.isSubmerged + JSON.stringify(lpcRenderProps.bodyFrame ?? { w: 64, h: 64 }) +
        (lpcRenderProps.heldObject ? JSON.stringify(lpcRenderProps.heldObject) : "") + (lpcRenderProps.frameOverrides ? "_frames:" + lpcRenderProps.frameOverrides.key : "")

    // Increment the live reference count for a key. Callers retain a key from the moment they REQUEST
    // its generation (see LpcCompositeSprite.doPreparation), NOT when they adopt the clone: generation
    // is async/time-budgeted and the baked textures are shared across sprites, so retaining only on
    // adopt leaves a request->adopt window in which eviction could destroy textures a soon-to-be-live
    // clone needs -- the clone then renders a destroyed texture and Pixi throws in calculateVertices.
    static retainKey = (key?: string): void => {
        if (!key) {
            return
        }
        SynchronousJob.refCounts.set(key, (SynchronousJob.refCounts.get(key) || 0) + 1)
    }

    // Decrement a key's reference count WITHOUT destroying any display object, then maybe evict.
    static releaseKey = (key?: string): void => {
        if (key) {
            const next = (SynchronousJob.refCounts.get(key) || 0) - 1
            if (next <= 0) {
                SynchronousJob.refCounts.delete(key)
            } else {
                SynchronousJob.refCounts.set(key, next)
            }
        }
        SynchronousJob.maybeEvict()
    }

    // Result-based convenience wrappers (used by tests and internal callers).
    static retain = (result?: LpcJobResult): void => {
        SynchronousJob.retainKey(result?.key)
    }

    // Decrement the count and destroy the clone's display object (textures untouched -- they belong
    // to the cache). May trigger eviction of now-unreferenced entries.
    static release = (result?: LpcJobResult): void => {
        SynchronousJob.destroyClone(result)
        SynchronousJob.releaseKey(result?.key)
    }

    // Dispose a clone's display object without touching the refcount: either a clone that was never
    // adopted (sprite discarded before the async callback), or an adopted clone torn down at cleanup
    // where its request-time key hold is released separately via releaseKey.
    static abandon = (result?: LpcJobResult): void => {
        SynchronousJob.destroyClone(result)
    }

    private static destroyClone = (result?: LpcJobResult): void => {
        if (!result) {
            return
        }
        try {
            result.animation?.destroy({ children: true, texture: false, baseTexture: false })
        } catch (e) {
            // ignore -- best-effort teardown
        }
        try {
            result.sprite?.destroy({ children: true, texture: false, baseTexture: false })
        } catch (e) {
            // ignore -- best-effort teardown
        }
    }

    // Evict unreferenced entries oldest-first until under the cap (or nothing evictable remains).
    static maybeEvict = (): void => {
        while (SynchronousJob.animationCache.size > SynchronousJob.maxCachedAnimations) {
            let evicted = false
            for (const key of SynchronousJob.animationCache.keys()) {
                if ((SynchronousJob.refCounts.get(key) || 0) <= 0) {
                    SynchronousJob.evictKey(key)
                    evicted = true
                    break
                }
            }
            if (!evicted) {
                // Everything over the cap is currently live; leave it and try again on next release.
                break
            }
        }
    }

    private static evictKey = (key: string): void => {
        const canonical = SynchronousJob.animationCache.get(key)
        SynchronousJob.animationCache.delete(key)
        SynchronousJob.refCounts.delete(key)

        // Destroy this key's baked frame RenderTextures (safe: no live clone references them).
        const prefix = `${key}-f-`
        for (const tKey of Array.from(SynchronousJob.textureCache.keys())) {
            if (tKey.startsWith(prefix)) {
                const tex = SynchronousJob.textureCache.get(tKey)
                SynchronousJob.textureCache.delete(tKey)
                try {
                    tex?.destroy(true)
                } catch (e) {
                    // ignore -- best-effort teardown
                }
            }
        }

        // Drop the cache-only canonical holder (its textures were destroyed just above).
        SynchronousJob.destroyClone(canonical)
    }

    static clearCaches = (): void => {
        SynchronousJob.textureCache.clear()
        SynchronousJob.animationCache.clear()
        SynchronousJob.refCounts.clear()
    }

    constructor(
        key: string,
        action: any,
        lpcRenderMeta: any,
        lpcRenderProps: LpcRenderProps,
        descriptors: LpcSheet[],
        callback: Callback<LpcJobResult>,
        cancelled?: () => boolean,
    ) {
        this.key = key
        this.action = action
        this.lpcRenderMeta = lpcRenderMeta
        this.lpcRenderProps = lpcRenderProps
        this.descriptors = descriptors
        this.callback = callback
        this.cancelled = cancelled
        this.startTs = Date.now()
    }

    isCancelled = (): boolean => this.generation !== contentGeneration() || this.rendererGeneration !== getRendererGeneration() || !!this.cancelled?.()

    process = () => {
        // The requesting sprite was discarded before this job ran (e.g. entity torn down on a room
        // change); skip the bake entirely -- nothing is waiting for the result.
        if (this.isCancelled()) {
            this.callback(null)
            return
        }

        const cachedResult = SynchronousJob.animationCache.get(this.key)
        if (cachedResult) {
            const source = this.lpcRenderMeta
            if (!source) {
                this.callback(null)
                return
            }

            // Refresh LRU recency so an in-use entry isn't evicted ahead of colder ones.
            SynchronousJob.animationCache.delete(this.key)
            SynchronousJob.animationCache.set(this.key, cachedResult)

            const result = SynchronousJob.cloneResult(cachedResult, source, this.key)
            this.result = result
            this.callback(result)
            return
        }
        let textureLayers = []

        const processTextureLayers = () => {
            const layers: Texture<Resource>[][] = textureLayers.sort((a, b) => a.order - b.order).map(n => n.textures)
            const animationFrameTextures = []

            // No layer had textures for this action (e.g. every sheet was skipped or failed to
            // load). Nothing to bake -- report failure instead of dereferencing layers[0].
            if (layers.length < 1 || !layers[0]) {
                this.callback(null)
                return
            }

            const frameCount = layers[0].length

            const body = this.lpcRenderProps.bodyFrame ?? { w: 64, h: 64 }
            let left = -body.w / 2, top = -body.h / 2, right = body.w / 2, bottom = body.h / 2
            const surfaces: Graphics[] = []
            for (let frame = 0; frame < frameCount; frame++) {
                const surface = new Graphics()
                const held = this.lpcRenderProps.heldObject &&
                    createHeldObjectSprite(this.lpcRenderProps.heldObject, this.action, frame)
                const heldBehind = heldObjectBehindBody(this.action)
                if (held) {
                    if (this.lpcRenderProps.isSubmerged) {
                        const mask = new Graphics()
                        mask.beginFill(0xffffff)
                        mask.drawRect(-40, -40, 80, 64)
                        mask.endFill()
                        held.addChild(mask)
                        held.mask = mask
                    }
                    if (heldBehind) {
                        surface.addChild(held)
                    }
                }
                for (let l = 0; l < layers.length; l++) {
                    const layerFrame = layers[l][frame]
                    // A layer with fewer frames than layer 0 has no texture here; skip it rather
                    // than baking an empty Sprite for it.
                    if (!layerFrame) {
                        continue
                    }
                    const sprite = new Sprite(layerFrame)
                    sprite.anchor.set(0.5, 0.5)
                    surface.addChild(sprite)

                    if (this.lpcRenderProps.isSubmerged) {
                        const mask = new Graphics()
                        mask.beginFill(0x000000, 1.0)
                        mask.drawRect(-40, -40, 80, 64)
                        mask.endFill()

                        sprite.addChild(mask)
                        sprite.mask = mask
                    }
                }
                // North/east/west carry behind the body; south carries above it.
                // Baking both in the same frame keeps the hand motion synchronized.
                if (held && !heldBehind) {
                    surface.addChild(held)
                }
                const bounds = surface.getLocalBounds()
                left = Math.min(left, bounds.x); top = Math.min(top, bounds.y)
                right = Math.max(right, bounds.x + bounds.width); bottom = Math.max(bottom, bounds.y + bounds.height)
                surfaces.push(surface)
            }
            // Integer edges preserve pixel sampling, even when held art has fractional bounds.
            const region = new Rectangle(Math.floor(left), Math.floor(top),
                Math.ceil(right) - Math.floor(left), Math.ceil(bottom) - Math.floor(top))
            surfaces.forEach((surface, frame) => {
                const textureKey = `${this.key}-f-${frame}`
                let texture = SynchronousJob.textureCache.get(textureKey)
                if (!texture) {
                    texture = getRendererApp().renderer.generateTexture(surface, { region })
                    SynchronousJob.textureCache.set(textureKey, texture)
                }
                animationFrameTextures.push(texture)
                surface.destroy({ children: true })
            })

            if (animationFrameTextures.length < 1) {
                this.callback(null)
                return
            }

            const source = this.lpcRenderMeta
            if (!source) {
                this.callback(null)
                return
            }

            // Keep all frames in the canonical cache; clones choose static or animated display.
            const canonicalAnimation = new AnimatedSprite(animationFrameTextures)
            canonicalAnimation.visible = false
            const canonical = new LpcJobResult({ animation: canonicalAnimation })
            canonical.key = this.key
            // Stamped here, on the cache entry, while we still know which sheets fed this action --
            // every clone (this caller's and every later cache hit's) then reads the same numbers.
            canonical.offsetX = region.x + body.w / 2
            canonical.offsetY = region.y + body.h / 2
            SynchronousJob.animationCache.set(this.key, canonical)
            // Safe to evict here: the caller retained this key at REQUEST time (see doPreparation),
            // so maybeEvict() cannot destroy the loadout we are about to clone and hand back.
            SynchronousJob.maybeEvict()

            const result = SynchronousJob.cloneResult(canonical, source, this.key)
            this.result = result
            this.callback(result)
            return canonical
        }

        this.descriptors.forEach((descriptor, i) => {
            const overrides = this.lpcRenderProps.frameOverrides?.sheets[descriptor.entityId]
            if (overrides !== undefined) {
                textureLayers.push({ order: i, textures: overrides })
                return
            }
            const sheetId = `lpc/spritesheets/${descriptor.entityId}.json`
            const url = `assets/${sheetId}`
            const textures = spriteSheetLoader.getAnimationTexturesFor(url, this.action)
            if (!textures) return

            const x = {
                order: i,
                textures,
            }
            textureLayers.push(x)
        })

        // processTextureLayers caches the canonical entry itself (and evicts if over cap).
        processTextureLayers()
    }

    getResult = (): LpcJobResult => this.result

    getKey = (): string => this.key
}

interface ProcessUnit {
    frameSurface: Graphics
    frame: number
    layer: number
    combine: boolean
}

class LpcCompositeGenerator {
    loader: Loader
    jobs: any[] = []
    timer: any
    // cache: Map<string, AnimatedSprite> = new Map()
    processing: Set<string> = new Set()
    latch: Latch = new Latch(1000)

    // Per-tick wall-clock budget (ms) for building composites. Instead of a fixed one-job-per-tick
    // cap, process() drains jobs until this much time is spent, always doing at least one so the
    // queue makes progress. This is self-scaling across hardware: cache-hit clones are nearly free
    // so many drain per tick, while an expensive generateTexture bake eats the budget -- so a weak
    // GPU naturally does ~1 bake/tick (no frame hitch) and a fast one does many (rooms populate
    // near-instantly). Kept small so a single tick can't blow a 60fps frame on low-end hardware.
    bakeBudgetMs = 4

    // Monotonic clock used for the budget. Prefers performance.now() (sub-ms, and NOT the mocked
    // Date.now this codebase uses for game time). Overridable in tests.
    now: () => number = () =>
        typeof performance !== "undefined" && performance.now ? performance.now() : Date.now()

    generate = (
        priority: boolean,
        action: any,
        lpcRenderMeta: any,
        lpcRenderProps: LpcRenderProps,
        descriptors: LpcSheet[],
        callback: Callback<LpcJobResult>,
        cancelled?: () => boolean,
    ) => {
        const key = SynchronousJob.computeKey(descriptors, action, lpcRenderProps)
        const job: Job = new SynchronousJob(
            key,
            action,
            lpcRenderMeta,
            lpcRenderProps,
            descriptors,
            callback,
            cancelled,
        )

        if (priority) {
            this.jobs.unshift(job)
        } else {
            this.jobs.push(job)
        }
        if (!this.timer) {
            this.timer = setInterval(this.process, INTERVAL)
        }
    }

    // Max cached loadout+action entries before unreferenced ones are evicted.
    get maxCachedAnimations(): number {
        return SynchronousJob.maxCachedAnimations
    }
    set maxCachedAnimations(n: number) {
        SynchronousJob.maxCachedAnimations = n
        SynchronousJob.maybeEvict()
    }

    // Mark a clone as live on screen (call when a sprite adopts a generate result).
    retain = (result?: LpcJobResult) => SynchronousJob.retain(result)

    // Mark a clone as gone (call when a sprite discards it); destroys the clone and may evict.
    release = (result?: LpcJobResult) => SynchronousJob.release(result)

    // Dispose a clone that was never adopted (sprite discarded before the async callback arrived).
    abandon = (result?: LpcJobResult) => SynchronousJob.abandon(result)

    // Retain a loadout key from the moment generation is REQUESTED (before the async job runs), so
    // eviction can't destroy its shared textures during the request->adopt window.
    retainKey = (key?: string) => SynchronousJob.retainKey(key)

    // Release a request-time key hold (refcount-only; does not destroy any display object).
    releaseKey = (key?: string) => SynchronousJob.releaseKey(key)

    // Compute the cache key for a loadout+action (same formula generate() uses).
    computeKey = (descriptors: LpcSheet[], action: any, lpcRenderProps: LpcRenderProps): string =>
        SynchronousJob.computeKey(descriptors, action, lpcRenderProps)

    // Clears the static composite caches (test isolation / teardown).
    clearCaches = () => SynchronousJob.clearCaches()

    process = () => {
        const start = this.now()

        // Drain jobs until the time budget is spent, always processing at least one so the queue
        // advances even if a single bake exceeds the budget on slow hardware.
        while (true) {
            // Drop cancelled jobs (sprite discarded, e.g. on a room change) cheaply -- they don't
            // count against the budget. Their callbacks get null inside process(), which released
            // sprites ignore.
            let current: Job = this.jobs.shift()
            while (current && current.isCancelled()) {
                current.process()
                current = this.jobs.shift()
            }

            if (!current) {
                clearInterval(this.timer)
                this.timer = undefined
                return
            }

            current.process()

            if (this.now() - start >= this.bakeBudgetMs) {
                return
            }
        }
    }
}

const lpcCompositeGenerator = new LpcCompositeGenerator()
export { lpcCompositeGenerator }
